import React, { useState, useMemo } from "react";
import { downloadFile } from "../../../utils/downloadFile";

export default function AdminBonusSheetsDownload() {
  const [downloadingStage5, setDownloadingStage5] = useState(false);
  const [downloadingClosing, setDownloadingClosing] = useState(false);

  // Initialize with last month as default
  const now = new Date();
  const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const [selectedMonth, setSelectedMonth] = useState(lastMonth.getMonth() + 1); // 1-12
  const [selectedYear, setSelectedYear] = useState(lastMonth.getFullYear());

  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1; // 1-12

  // Stage5 minimum year is 2026
  const stage5MinYear = 2026;
  const stage5MinMonth = 1; // January 2026

  const isFutureDate = useMemo(() => {
    if (selectedYear > currentYear) return true;
    if (selectedYear === currentYear && selectedMonth > currentMonth) return true;
    return false;
  }, [selectedYear, selectedMonth, currentYear, currentMonth]);

  const isStage5Valid = useMemo(() => {
    return selectedYear >= stage5MinYear;
  }, [selectedYear]);

  const yearOptions = useMemo(() => {
    const years = new Set<number>();
    
    years.add(currentYear);
    if (currentYear > 2024) {
      years.add(currentYear - 1);
    }
    
    // Add 2026 and later years (for Stage5)
    if (currentYear >= stage5MinYear) {
      for (let year = currentYear; year >= stage5MinYear; year--) {
        years.add(year);
      }
    } else {
      years.add(stage5MinYear);
    }
    
    return Array.from(years).sort((a, b) => b - a); // Sort descending
  }, [currentYear]);

  // filter out future months and handle Stage5 restrictions
  const monthOptions = useMemo(() => {
    const months = Array.from({ length: 12 }, (_, i) => i + 1);
    
    if (selectedYear === currentYear) {
      return months.filter(month => month <= currentMonth);
    }
    
    if (selectedYear === stage5MinYear) {
      return months.filter(month => month >= stage5MinMonth);
    }
    
    // For past years, show all months
    return months;
  }, [selectedYear, currentYear, currentMonth]);

  // Format selected month/year for display
  const selectedMonthFormatted = useMemo(() => {
    const monthName = new Date(selectedYear, selectedMonth - 1, 1).toLocaleString('en-US', { month: 'long' });
    return `${monthName}-${selectedYear}`;
  }, [selectedMonth, selectedYear]);

  const handleYearChange = (year: number) => {
    setSelectedYear(year);
    if (year === currentYear && selectedMonth > currentMonth) {
      setSelectedMonth(1);
    } else if (year === stage5MinYear && selectedMonth < stage5MinMonth) {
      setSelectedMonth(stage5MinMonth);
    }
  };

  // Format month name for filename/api/cba/bonuses/stage5-calculations-excel
  const getMonthName = (month: number) => {
    return new Date(selectedYear, month - 1, 1).toLocaleString('en-US', { month: 'long' });
  };

  const handleDownloadStage5 = async () => {
    try {
      setDownloadingStage5(true);
      const monthName = getMonthName(selectedMonth);
      await downloadFile(
        "/api/cba/bonuses/stage5-monthly-bonus-sheets-download",
        `Stage5_Bonus_Sheets_${selectedYear}_${monthName}.xlsx`,
        null,
        { month: selectedMonth, year: selectedYear }
      );
    } finally {
      setDownloadingStage5(false);
    }
  };

  const handleDownloadClosing = async () => {
    try {
      setDownloadingClosing(true);
      const monthName = getMonthName(selectedMonth);
      await downloadFile(
        "/api/cba/bonuses/closing-calculations-excel",
        `ClosingCalculations_${monthName}-${selectedYear}.xlsx`,
        null,
        { month: selectedMonth, year: selectedYear }
      );
    } finally {
      setDownloadingClosing(false);
    }
  };

  return (
    <div className="p-4">
      <h1 className="text-xl font-semibold mb-4">Bonus Sheets Download</h1>
      
      <div className="mb-6 space-y-4">
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium text-gray-700">Select Month:</label>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 min-w-[150px]"
            >
              {monthOptions.map((month) => (
                <option key={month} value={month}>
                  {new Date(selectedYear, month - 1, 1).toLocaleString('en-US', { month: 'long' })}
                </option>
              ))}
            </select>

            <label className="text-sm font-medium text-gray-700">Select Year:</label>
            <select
              value={selectedYear}
              onChange={(e) => handleYearChange(parseInt(e.target.value))}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 min-w-[120px]"
            >
              {yearOptions.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>
          
          {isFutureDate && (
            <p className="text-sm text-red-600">
              Warning: Future dates are not allowed. Please select a past or current month.
            </p>
          )}
          
          <p className="text-sm text-gray-600">
            Download bonus details for the month of {selectedMonthFormatted}
          </p>
        </div>
      </div>
      
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium">Stage5 Bonus Sheet:</span>
          <button
            onClick={handleDownloadStage5}
            disabled={downloadingStage5 || !isStage5Valid || isFutureDate}
            className={`px-4 py-2 rounded-md text-white ${
              downloadingStage5 || !isStage5Valid || isFutureDate
                ? "bg-gray-400 cursor-not-allowed" 
                : "bg-primary-500 hover:bg-primary-600"
            }`}
            title={!isStage5Valid ? "Stage5 downloads are only available from January 2026 onwards" : isFutureDate ? "Future dates are not allowed" : ""}
          >
            {downloadingStage5 ? "Preparing..." : "Download"}
          </button>
          {!isStage5Valid && (
            <span className="text-sm text-red-600">
              (Only available from January 2026 onwards)
            </span>
          )}
        </div>

        <div className="flex items-center gap-3">
          <span className="text-sm font-medium">Closing Bonus Sheet:</span>
          <button
            onClick={handleDownloadClosing}
            disabled={downloadingClosing || isFutureDate}
            className={`px-4 py-2 rounded-md text-white ${
              downloadingClosing || isFutureDate
                ? "bg-gray-400 cursor-not-allowed" 
                : "bg-primary-500 hover:bg-primary-600"
            }`}
            title={isFutureDate ? "Future dates are not allowed" : ""}
          >
            {downloadingClosing ? "Preparing..." : "Download"}
          </button>
        </div>
      </div>
    </div>
  );
}